// cleancode-ui/src/components/Settings.jsx
import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import TrialStatus from './TrialStatus';
import DowngradeModal from './DowngradeModal';
import { useEnvironment } from '../contexts/EnvironmentContext';
import './Login.css';

const Settings = ({ onLogout }) => {
  const { apiBaseUrl } = useEnvironment();
  const [activeTab, setActiveTab] = useState('account');
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [planPricing, setPlanPricing] = useState({});
  const [trialInfo, setTrialInfo] = useState(null);
  const [downgradeModal, setDowngradeModal] = useState({
    isOpen: false,
    currentPlan: null,
    targetPlan: null
  });
  const [cancelModal, setCancelModal] = useState({
    isOpen: false
  });

  useEffect(() => {
    fetchUserData();
    fetchSettings();
    fetchPlanPricing();
    fetchTrialStatus();
    
    // Check if we just connected to GitHub
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('github') === 'connected') {
      // Refresh settings to show updated GitHub connection status
      setTimeout(() => {
        fetchSettings();
        // Clean up URL parameter
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000);
    }
    
    // Check if we just completed a subscription upgrade
    if (urlParams.get('upgraded') === 'true') {
      // Refresh user data to show updated plan
      setTimeout(() => {
        fetchUserData();
        // Clean up URL parameter
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000);
    }
  }, []);

  const fetchUserData = async () => {
    try {
      // Get the stored email from localStorage
      const storedEmail = localStorage.getItem('userEmail') || 'user@example.com';
      
      const response = await fetch(`${apiBaseUrl}/api/user/profile?email=${encodeURIComponent(storedEmail)}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }

      const data = await response.json();
      setUser(data.user);
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Get email from localStorage if available, otherwise use mock data
      const storedEmail = localStorage.getItem('userEmail') || 'user@example.com';
      setUser({
        id: 1,
        email: storedEmail,
        name: 'John Doe',
        plan: 'pro',
        createdAt: '2024-01-01T00:00:00Z'
      });
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/user/settings`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      setSettings(data.settings || {});
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use mock data for now
      setSettings({
        emailNotifications: true,
        theme: 'light',
        githubConnected: false,
        language: 'en'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanPricing = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/billing/all-plan-pricing`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch plan pricing');
      }

      const data = await response.json();
      setPlanPricing(data.plans || {});
    } catch (error) {
      console.error('Error fetching plan pricing:', error);
      // Use fallback pricing if API fails
      setPlanPricing({
        'free': { monthly: 0, currency: 'usd' },
        'solo': { monthly: 12, currency: 'usd' },
        'pro': { monthly: 25, currency: 'usd' }
      });
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/billing/trial-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTrialInfo(data);
      }
    } catch (error) {
      console.error('Error fetching trial status:', error);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/user/settings`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ settings: newSettings })
      });

      if (!response.ok) {
        throw new Error('Failed to update settings');
      }

      setSettings(newSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings');
    }
  };

  const handleGitHubDisconnect = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/github/disconnect`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect GitHub');
      }

      // Refresh settings to get updated connection status
      await fetchSettings();
    } catch (error) {
      console.error('Error disconnecting GitHub:', error);
      alert('Failed to disconnect GitHub');
    }
  };

  const handleManageBilling = async () => {
    try {
      // Get user's email from localStorage
      const userEmail = localStorage.getItem('userEmail') || 'test@example.com';
      
      const response = await fetch(`${apiBaseUrl}/billing/create-customer-portal-session?email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create billing portal session');
      }

      const data = await response.json();
      
      // Check if it's the Stripe dashboard URL (which we don't want)
      if (data.url && data.url.includes('dashboard.stripe.com')) {
        alert('Billing portal is not yet configured. Please contact support to set up billing management.');
      } else if (data.url && data.url.includes('billing.stripe.com')) {
        // It's a real customer portal URL, redirect to it
        window.location.href = data.url;
      } else if (data.error) {
        // Show error message
        alert(`${data.message}\n\n${data.details || ''}`);
      } else {
        alert('Failed to create billing session');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    }
  };

  const handleDowngradeClick = (currentPlan, targetPlan) => {
    setDowngradeModal({
      isOpen: true,
      currentPlan,
      targetPlan
    });
  };

  const handleDowngradeConfirm = async (selectedPlan) => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      const response = await fetch(`${apiBaseUrl}/billing/change-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          email: userEmail,
          plan: selectedPlan || downgradeModal.targetPlan
        })
      });

      const result = await response.json();

      if (result.success) {
        alert(`Successfully downgraded to ${result.new_plan} plan! Changes will take effect at your next billing cycle.`);
        // Refresh user data to show updated plan
        fetchUserData();
      } else {
        const errorMessage = result.suggestion 
          ? `${result.message}\n\n${result.suggestion}` 
          : result.message;
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error changing plan:', error);
      alert('Failed to change plan. Please try again or use the billing portal.');
      // Fallback to billing portal
      handleManageBilling();
    }
  };

  const handleDowngradeModalClose = () => {
    setDowngradeModal({
      isOpen: false,
      currentPlan: null,
      targetPlan: null
    });
  };

  const handleCancelSubscription = async () => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      const response = await fetch(`${apiBaseUrl}/billing/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          email: userEmail
        })
      });

      const result = await response.json();

      if (result.success) {
        alert(result.message); // Use the dynamic message from the API
        
        // Check if logout is required after cancellation
        if (result.logout_required) {
          // Clear all user data from localStorage
          localStorage.removeItem('token');
          localStorage.removeItem('userEmail');
          
          // Redirect to login page
          window.location.href = '/login';
        } else {
          // Refresh user data to show updated plan
          fetchUserData();
          fetchTrialStatus();
        }
        
        setCancelModal({ isOpen: false });
      } else {
        const errorMessage = result.suggestion 
          ? `${result.message}\n\n${result.suggestion}` 
          : result.message;
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    }
  };

  const handleCancelModalClose = () => {
    setCancelModal({ isOpen: false });
  };

  const handleUpgradePlan = async () => {
    try {
      // Determine the next plan to upgrade to
      const currentPlan = user?.plan || 'free';
      let nextPlan = 'pro'; // Default upgrade to Pro
      
      if (currentPlan === 'free') {
        nextPlan = 'solo';
      } else if (currentPlan === 'solo') {
        nextPlan = 'pro';
      }

      // Create checkout session for the upgrade
      const response = await fetch(`${apiBaseUrl}/billing/create-checkout-session/${nextPlan}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        alert('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start upgrade process. Please try again.');
    }
  };

  const tabs = [
    { key: 'account', label: 'Account', icon: '👤' },
    { key: 'notifications', label: 'Notifications', icon: '🔔' },
    { key: 'integrations', label: 'Integrations', icon: '🔗' },
    { key: 'billing', label: 'Billing', icon: '💳' }
  ];

  if (loading) {
    return (
      <div>
        <Navigation onLogout={onLogout} />
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1 className="login-title">Settings</h1>
              <p className="login-subtitle">Loading your preferences...</p>
            </div>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>
        {`
          .settings-tabs::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      <Navigation onLogout={onLogout} />
      <TrialStatus />
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="login-title">Settings</h1>
            <p className="login-subtitle">Configure your preferences and account</p>
          </div>

          <div className="dashboard-content">
            {/* Tab navigation */}
            <div className="settings-tabs" style={{ 
              display: 'flex', 
              borderBottom: '1px solid #ddd', 
              marginBottom: '2rem',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitScrollbar: { display: 'none' }
            }}>
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
                  style={{
                    padding: '1rem 1.5rem',
                    border: 'none',
                    background: 'none',
                    borderBottom: activeTab === tab.key ? '2px solid #4A90E2' : '2px solid transparent',
                    color: activeTab === tab.key ? '#4A90E2' : '#666',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                    flexShrink: 0
                  }}
                >
                  <span style={{ marginRight: '0.5rem' }}>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="tab-content">
              {activeTab === 'account' && (
                <div className="account-settings">
                  <h3 style={{ marginBottom: '1rem' }}>Account Information</h3>
                  <div className="setting-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '0.5rem',
                        background: '#f8f9fa'
                      }}
                    />
                  </div>
                  <div className="setting-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Current Plan
                    </label>
                    
                    {/* Plan Display */}
                    <div style={{
                      padding: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '0.5rem',
                      background: '#f8f9fa',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ textTransform: 'capitalize', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {user?.plan || 'free'} Plan
                        </span>
                        <button 
                          style={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontSize: '0.7rem',
                            padding: '0.4rem 0.8rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem'
                          }}
                          onClick={() => {
                            console.log('Refreshing user data...');
                            fetchUserData();
                          }}
                          onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                          }}
                        >
                          <span style={{ fontSize: '0.8rem' }}>🔄</span>
                          Refresh
                        </button>
                      </div>
                      
                      {/* Debug info */}
                      {console.log('Settings: Current user plan:', user?.plan, 'Type:', typeof user?.plan)}
                      <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '0.5rem' }}>
                        Debug: Current plan = "{user?.plan || 'free'}" | Email = "{user?.email || 'unknown'}"
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {user?.plan !== 'pro' && (
                        <button 
                          className="login-button" 
                          style={{ background: '#4A90E2', fontSize: '0.9rem', padding: '0.6rem 1.2rem' }}
                          onClick={handleUpgradePlan}
                        >
                          Upgrade Plan
                        </button>
                      )}
                      
                      {user?.plan !== 'free' && (
                        <button 
                          style={{ 
                            background: trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 
                              ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                              : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontSize: '0.9rem',
                            padding: '0.6rem 1.2rem',
                            cursor: trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 ? 'not-allowed' : 'pointer',
                            fontWeight: '500',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            opacity: trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 ? 0.6 : 1
                          }}
                          onClick={() => {
                            if (trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0) {
                              alert('You cannot downgrade during your trial period. Please wait until your trial ends.');
                              return;
                            }
                            const currentPlan = user?.plan || 'free';
                            const downgradeTarget = currentPlan === 'pro' ? 'solo' : 'free';
                            handleDowngradeClick(currentPlan, downgradeTarget);
                          }}
                          onMouseOver={(e) => {
                            if (!(trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0)) {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                            }
                          }}
                          onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                          }}
                        >
                          {trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 
                            ? 'Downgrade (After Trial)' 
                            : 'Downgrade'
                          }
                        </button>
                      )}
                      
                      {user?.plan === 'pro' && (
                        <span style={{ 
                          fontSize: '0.9rem', 
                          color: '#28a745', 
                          fontWeight: 'bold',
                          background: '#d4edda',
                          padding: '0.6rem 1rem',
                          borderRadius: '0.5rem',
                          border: '1px solid #c3e6cb',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span>✓</span>
                          Highest Plan
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="notification-settings">
                  <h3 style={{ marginBottom: '1rem' }}>Notification Preferences</h3>
                  <div className="setting-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications || false}
                        onChange={(e) => updateSettings({ ...settings, emailNotifications: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Email notifications for review completions
                    </label>
                  </div>
                  <div className="setting-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Theme
                    </label>
                    <select
                      value={settings.theme || 'light'}
                      onChange={(e) => updateSettings({ ...settings, theme: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '0.5rem'
                      }}
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto</option>
                    </select>
                  </div>
                </div>
              )}

              {activeTab === 'integrations' && (
                <div className="integration-settings">
                  <h3 style={{ marginBottom: '1rem' }}>Third-Party Integrations</h3>
                  <div className="integration-item" style={{
                    border: '1px solid #ddd',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.25rem 0' }}>GitHub</h4>
                      <p style={{ margin: '0', fontSize: '0.9rem', color: '#666' }}>
                        Connect your GitHub account to analyze pull requests
                      </p>
                    </div>
                    <div>
                      {settings.githubConnected ? (
                        <button
                          className="login-button"
                          style={{ background: '#dc3545', fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                          onClick={handleGitHubDisconnect}
                        >
                          Disconnect
                        </button>
                      ) : (
                        <button
                          className="login-button"
                          style={{ background: '#28a745', fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                          onClick={() => window.location.href = `${apiBaseUrl}/oauth/github/login`}
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'billing' && (
                <div className="billing-settings">
                  <h3 style={{ marginBottom: '1rem' }}>Billing & Subscription</h3>
                  <div className="billing-info" style={{
                    border: '1px solid #ddd',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0' }}>Current Plan: {user?.plan || 'free'}</h4>
                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#666' }}>
                      Manage your subscription and billing information
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <button 
                        className="login-button" 
                        style={{ background: '#4A90E2', fontSize: '0.9rem', padding: '0.75rem 1.5rem' }}
                        onClick={handleManageBilling}
                      >
                        Manage Billing
                      </button>
                      
                      {user?.plan !== 'free' && (
                        <button 
                          className="login-button" 
                          style={{ 
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            fontSize: '0.9rem', 
                            padding: '0.75rem 1.5rem',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => setCancelModal({ isOpen: true })}
                          onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                          }}
                        >
                          Cancel {trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 ? 'Trial' : 'Subscription'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Downgrade Confirmation Modal */}
      <DowngradeModal
        isOpen={downgradeModal.isOpen}
        onClose={handleDowngradeModalClose}
        onConfirm={handleDowngradeConfirm}
        currentPlan={downgradeModal.currentPlan}
        targetPlan={null}
        planPricing={planPricing}
      />

      {/* Cancel Subscription Confirmation Modal */}
      {cancelModal.isOpen && (
        <div className="modal-overlay" onClick={handleCancelModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel {trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 ? 'Trial' : 'Subscription'}</h3>
              <button className="modal-close" onClick={handleCancelModalClose}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', fontSize: '1rem', lineHeight: '1.5' }}>
                Are you sure you want to cancel your {trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 ? 'trial' : 'subscription'}? This action cannot be undone and you will lose access to premium features immediately.
              </p>
              <div style={{ 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '0.5rem', 
                padding: '1rem', 
                marginBottom: '1rem' 
              }}>
                <p style={{ margin: 0, color: '#dc2626', fontSize: '0.9rem' }}>
                  <strong>⚠️ Important:</strong> Once cancelled, you'll need to subscribe again to regain access to premium features.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-cancel" 
                onClick={handleCancelModalClose}
                style={{ 
                  background: '#6b7280', 
                  color: 'white', 
                  border: 'none', 
                  padding: '0.75rem 1.5rem', 
                  borderRadius: '0.5rem', 
                  cursor: 'pointer',
                  marginRight: '0.75rem'
                }}
              >
                Keep {trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 ? 'Trial' : 'Subscription'}
              </button>
              <button 
                className="btn-confirm" 
                onClick={handleCancelSubscription}
                style={{ 
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white', 
                  border: 'none', 
                  padding: '0.75rem 1.5rem', 
                  borderRadius: '0.5rem', 
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Yes, Cancel {trialInfo?.has_trial && trialInfo?.trial_days_remaining > 0 ? 'Trial' : 'Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
